import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Bell, Sun, Moon, LogOut, User as UserIcon, Shield, Lock, Building } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { useTheme } from '../../context/ThemeContext.js';
import { useNotifications } from '../../context/NotificationContext.js';
import { Avatar } from '../ui/Badge.js';
import './layout.css';

interface HeaderProps {
  onOpenSearch: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onOpenSearch }) => {
  const { user, logout, logoutAll } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount, toggleDrawer } = useNotifications();
  const location = useLocation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const getBreadcrumbTitle = (path: string) => {
    switch (path) {
      case '/dashboard': return 'Platform Overview';
      case '/users': return 'User Directory';
      case '/roles': return 'Roles & Access Matrix';
      case '/departments': return 'Departments';
      case '/settings': return 'Company Settings';
      case '/audit': return 'Audit Logs';
      case '/finance/dashboard': return 'Finance Analytics';
      case '/finance/coa': return 'Chart of Accounts';
      case '/finance/transactions': return 'Transaction Ledger';
      case '/finance/invoices': return 'Invoices & Billing';
      case '/finance/expenses': return 'Expense Claims';
      case '/finance/budgets': return 'Budgets & Variance';
      case '/finance/investments': return 'Owner Capital';
      case '/finance/reports': return 'Financial Reports';
      case '/hrms/dashboard': return 'Workforce Overview';
      case '/hrms/attendance': return 'Time & Attendance';
      case '/hrms/leave': return 'Leave Management';
      case '/hrms/daily-reports': return 'Daily Work Reports';
      case '/hrms/tasks': return 'Studio Task Board';
      case '/hrms/performance': return 'Performance Scorecards';
      case '/hrms/announcements': return 'Studio Notice Board';
      case '/crm/dashboard': return 'Sales & CRM Analytics';
      case '/crm/leads': return 'Leads & Pipeline';
      case '/crm/clients': return 'Client Directory';
      case '/crm/quotations': return 'Quotations & Proposals';
      case '/crm/meetings': return 'Client Consultations';
      case '/crm/services': return 'Studio Service Catalog';
      case '/projects/dashboard': return 'Operations Overview';
      case '/projects/list': return 'Project Kanban & List';
      case '/projects/deliverables': return 'Deliverables & Approvals';
      case '/projects/resources': return 'Studio Resource Booking';
      default: return 'Project & Operations System';
    }
  };

  return (
    <header className="app-header glass-effect">
      {/* Top Left: Logo Icon & Workspace Selector */}
      <div className="header-left">
        <div className="header-brand-badge">
          <img
            src="/drishti-logo-icon.png"
            alt="Drishti Studios Mark"
            className="header-logo-icon"
          />
          <div className="header-workspace-chip" title="Active Workspace">
            <Building size={14} className="workspace-icon" />
            <span className="workspace-name">Drishti Studios HQ</span>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="breadcrumb-box">
          <span className="breadcrumb-root">Drishti OS</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{getBreadcrumbTitle(location.pathname)}</span>
        </div>
      </div>

      {/* Top Right: Search, Notifications, Theme, User Profile */}
      <div className="header-right">
        {/* Global Search Trigger */}
        <button className="search-trigger-btn" onClick={onOpenSearch}>
          <Search size={16} />
          <span className="search-trigger-text">Search projects, deliverables, resources...</span>
          <kbd className="search-kbd">Ctrl K</kbd>
        </button>

        {/* Notification Bell */}
        <button className="icon-btn" onClick={toggleDrawer} title="Notifications">
          <Bell size={18} />
          {unreadCount > 0 && <span className="notification-badge-dot">{unreadCount}</span>}
        </button>

        {/* Theme Toggle */}
        <button className="icon-btn" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}>
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* User Profile Menu */}
        {user && (
          <div className="profile-menu-container">
            <button
              className="profile-btn"
              onClick={() => setProfileMenuOpen((prev) => !prev)}
            >
              <Avatar name={user.name} size="sm" statusDot="ACTIVE" />
              <div className="profile-btn-info">
                <span className="profile-btn-name">{user.name}</span>
                <span className="profile-btn-role">{user.role?.name || 'Administrator'}</span>
              </div>
            </button>

            {profileMenuOpen && (
              <div className="profile-dropdown glass-effect" onClick={() => setProfileMenuOpen(false)}>
                <div className="dropdown-user-header">
                  <p className="user-dropdown-name">{user.name}</p>
                  <p className="user-dropdown-email">{user.email}</p>
                  <span className="user-dropdown-empid">ID: {user.empId}</span>
                </div>
                <div className="dropdown-divider" />
                <div className="dropdown-item">
                  <UserIcon size={16} />
                  <span>Profile Overview</span>
                </div>
                <div className="dropdown-item">
                  <Shield size={16} />
                  <span>Role: {user.role?.name || 'Administrator'}</span>
                </div>
                <div className="dropdown-divider" />
                <div className="dropdown-item danger" onClick={logout}>
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </div>
                <div className="dropdown-item danger" onClick={logoutAll}>
                  <Lock size={16} />
                  <span>Sign Out All Devices</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
